import { AnimatePresence, motion } from "framer-motion"
import { MentoriaCard } from "@/modules/Mentorings/components/MentoriaCard"
import { MentoriaDataType } from "@/types/mentorias"

interface MentoringsTabContentProps {
  allMentorings: MentoriaDataType[]
  highlightedMentoringId: number | null
  isScheduledOpen: boolean
  onToggleScheduledOpen: () => void
  onRate: (mentoria: MentoriaDataType) => void
}

export function MentoringsTabContent({
  allMentorings,
  highlightedMentoringId,
  isScheduledOpen,
  onToggleScheduledOpen,
  onRate,
}: MentoringsTabContentProps) {
  let scheduledMentorings = allMentorings.filter((m) => m.status_mentoria === 1)
  const historyMentorings = allMentorings.filter((m) => m.status_mentoria !== 1)

  if (highlightedMentoringId) {
    scheduledMentorings = [...scheduledMentorings].sort((a, b) => {
      if (a.mentoria_id === highlightedMentoringId) return -1
      if (b.mentoria_id === highlightedMentoringId) return 1
      return 0
    })
  }

  return (
    <div className="flex flex-col gap-16 py-8">
      {scheduledMentorings.length > 0 && (
        <section className="space-y-8">
          <button
            className="group flex w-full items-center gap-4 outline-none"
            onClick={onToggleScheduledOpen}
          >
            <h2 className="text-2xl font-black uppercase tracking-tight text-white transition-colors">
              Agendadas
            </h2>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/5 transition-all group-hover:bg-white/10">
              <motion.i
                animate={{ rotate: isScheduledOpen ? 180 : 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="fi fi-ts-angle-small-down flex items-center justify-center text-white/50"
              />
            </div>
          </button>
          <AnimatePresence initial={false}>
            {isScheduledOpen && (
              <motion.div
                key="scheduled-content"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                className="overflow-hidden"
              >
                <div
                  style={
                    {
                      "--max-width": scheduledMentorings.length <= 2 ? "400px" : "1fr",
                    } as React.CSSProperties
                  }
                  className="grid grid-cols-[repeat(auto-fit,minmax(calc(5vw+320px),var(--max-width)))] gap-6 pb-4"
                >
                  {scheduledMentorings.map((mentoria) => (
                    <MentoriaCard
                      key={mentoria.mentoria_id}
                      mentoria={mentoria}
                      isHighlighted={mentoria.mentoria_id === highlightedMentoringId}
                      onRate={onRate}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      )}
      <section className="space-y-8">
        <div className="flex items-center gap-4 border-b border-white/5 pb-4">
          <h2 className="text-xl font-bold uppercase tracking-tight text-white/50">Histórico</h2>
        </div>
        {historyMentorings.length > 0 ? (
          <div
            style={
              {
                "--max-width": historyMentorings.length <= 2 ? "400px" : "1fr",
              } as React.CSSProperties
            }
            className="grid grid-cols-[repeat(auto-fit,minmax(calc(5vw+300px),var(--max-width)))] gap-6"
          >
            {historyMentorings.map((mentoria) => (
              <MentoriaCard
                key={mentoria.mentoria_id}
                mentoria={mentoria}
                isHistory
                onRate={onRate}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-white/5 bg-white/[0.01] py-20">
            <i className="fi fi-rr-box-open mb-3 text-3xl text-white/10" />
            <p className="text-sm font-medium text-white/20">
              Nenhum registro encontrado no histórico.
            </p>
          </div>
        )}
      </section>
    </div>
  )
}
